import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { useSelector } from 'react-redux';
import { IonContent, IonPage, useIonViewDidEnter, useIonViewDidLeave } from '@ionic/react';
import { Container } from '@mindly/ui-components';
import { getOnBoardingID, getOnBoardingName, getStepNumber, getVars } from '../../../../redux/onBoarding/selector';
import { ResultInfoWrapper } from './OnBoardingResultInfo.style';
import useOnBoardingCurrentStep from '../../../../hooks/useOnBoardingCurrentStep';
import { OnBoardingTypes } from '../../../../redux/onBoarding/types';
import { RouteComponentProps } from 'react-router-dom';
import useOnBoardingNextStep from 'src/hooks/useOnBoardingNextStep';
import { useIntl } from 'react-intl';
import { FunnelTypeEnum, useAnalytics } from 'src/shared';

import { setSplashColor } from 'src/shared/utils/splashColor';
import { globalVariables } from 'src/redux/firestoreGlobalVariables/selector';
import { useTranslation } from 'react-i18next';
const INTERVAL = 3000;

interface OnBoardingResultInfoProps extends RouteComponentProps<{ boardId: string; id: string }> {}

const OnBoardingResultInfo: FC<OnBoardingResultInfoProps> = ({ match }) => {
  const { id } = match.params;
  const [step, setStep] = useState(0);
  const [isFinish, setFinish] = useState(false);
  const [isPageView, setIsPageView] = useState(false);
  const [isEventTracked, setIsEventTracked] = useState(false);

  const vars = useSelector(getVars);
  const onBoardingName = useSelector(getOnBoardingName);
  const stepNumber = useSelector(getStepNumber);
  const onBoardingId = useSelector(getOnBoardingID);
  const globalVars = useSelector(globalVariables);

  const intervalRef = useRef<NodeJS.Timeout | undefined>();
  const pageCounterRef = useRef(0);
  const { i18n } = useTranslation();

  const currentStepAction = useOnBoardingCurrentStep({
    ...match.params,
    type: OnBoardingTypes.RESULT_INFO,
  });

  const nextStepAction = useOnBoardingNextStep();
  const intl = useIntl();
  const { trackPredefinedEvent } = useAnalytics();

  const data = useMemo(
    () => [
      intl.formatMessage({
        id: 'on-boarding.result.info.item.1',
        defaultMessage: 'На основі Ваших відповідей...',
      }),
      intl.formatMessage({
        id: 'on-boarding.result.info.item.2',
        defaultMessage: 'Та на основі тисяч успішних користувачів Mindly...',
      }),
      intl.formatMessage({
        id: 'on-boarding.result.info.item.3',
        defaultMessage: 'Ми підібрали ідеальних психологів для Вас...',
      }),
    ],
    [vars, intl]
  );

  useIonViewDidEnter(async () => {
    currentStepAction();
    setSplashColor('#fffffe');

    setIsPageView(true);
    setIsEventTracked(false);

    intervalRef.current = setInterval(() => {
      setStep((prev) => (data.length === prev ? prev : prev + 1));
    }, INTERVAL);

    if (isFinish) return;

    setTimeout(
      () => {
        setFinish(true);
      },
      INTERVAL * data.length + 1000
    );
  }, [currentStepAction, data, isFinish]);

  useIonViewDidLeave(() => {
    clearInterval(intervalRef.current);
    setIsPageView(false);
    setIsEventTracked(false);
    pageCounterRef.current = 0;
  });

  const handleNextStep = useCallback(async () => {
    await nextStepAction();
  }, [nextStepAction]);

  useEffect(() => {
    if (step === data.length - 1) {
      clearInterval(intervalRef.current);
    }
  }, [step, data]);

  useEffect(() => {
    if (!isPageView) return;
    if (pageCounterRef.current > 0) return;
    pageCounterRef.current += 1;
  }, [isPageView, stepNumber, onBoardingName, pageCounterRef]);

  useEffect(() => {
    if (isFinish) {
      handleNextStep();
    }
  }, [isFinish]);

  const handleAnalyticEvent = () => {
    if (!onBoardingId) return;

    trackPredefinedEvent('FUNNEL_SCREEN_VIEWED', {
      funnel_id: onBoardingId,
      funnel_name: onBoardingName,
      funnel_type:
        onBoardingId === globalVars?.defaultOnBoardingId?.[i18n.language]
          ? FunnelTypeEnum.DEFAULT
          : FunnelTypeEnum.WEB_TO_APP,
      screen_id: id,
      screen_name: `Result info screen: ${id}`,
      screen_order: stepNumber,
    });
    setIsEventTracked(true);
  };

  useEffect(() => {
    if (!isPageView) return;
    if (!onBoardingId || !onBoardingName || !stepNumber) return;
    if (isEventTracked) return;

    handleAnalyticEvent();
  }, [isPageView, onBoardingId, onBoardingName, stepNumber, isEventTracked]);

  return (
    <IonPage>
      <IonContent className={'white'}>
        <Container>
          <ResultInfoWrapper>
            <SwitchTransition mode="out-in">
              <CSSTransition key={step} timeout={300} classNames="infoFade">
                <div
                  className="info-text"
                  dangerouslySetInnerHTML={{
                    __html:
                      data[step]?.replace(/{{(\w*)}}/g, (m, key) => (vars.hasOwnProperty(key) ? vars[key] : m)) ?? '',
                  }}
                />
              </CSSTransition>
            </SwitchTransition>
          </ResultInfoWrapper>
        </Container>
      </IonContent>
    </IonPage>
  );
};

export default React.memo(OnBoardingResultInfo);
